/**
 * The System Role vocabulary overlay (NG-1246, PRD §2.1).
 *
 * The product uses one word — "Role" — for three unrelated things: the
 * capability bundle a workspace admin configures, a member's **Job Role** (their
 * job title) and their **Team Role** (Lead vs Member). Epic NG-1233 makes the
 * first of those customer-editable, so an admin creating a "Role" has no way to
 * tell which of the three they are configuring. The PRD renames the capability
 * bundle to **System Role** and leaves the other two alone.
 *
 * It ships as an overlay rather than an edit to `en.ts` / `fr.ts` because every
 * UI change merges dark behind a LaunchDarkly flag (root `CLAUDE.md` → "Feature
 * flags"), and the alternative — a flag check at each of the ~30 call sites —
 * would put a conditional on every label this epic exists to clarify. The
 * translation store consults this map ahead of the locale map when
 * `CUSTOM_SYSTEM_ROLES_FLAG` is on, so nothing outside `store.ts` knows the flag
 * exists. See `TranslationStore.setVocabularyOverlay`.
 *
 * **Only keys that name the capability bundle belong here.** Adding a Job Role,
 * Team Role, project-role or team-role key would rename something the PRD
 * deliberately leaves untouched. The four dead keys in this area
 * (`user.role`, `workspace_settings.members.invitations_role`,
 * `…members.custom_role_updated`, `…members.custom_role_update_error`) have no
 * `t()` caller and are left out on purpose — an overlay entry for a string
 * nothing renders is a claim that cannot be verified.
 */

import type { Locale } from "../types";

/**
 * Keys whose value names the capability bundle, per locale. Every key must
 * already exist in the matching locale file — `t()` falls back to the locale map
 * for anything absent here, so a typo'd key is silently inert rather than loud.
 * `system_role_vocabulary.test.ts` asserts that correspondence.
 */
export const SYSTEM_ROLE_VOCABULARY: Record<Locale, Record<string, string>> = {
  en: {
    // Workspace settings navigation
    "workspace_settings.tabs.roles": "System Roles",

    // System Roles page
    "workspace_settings.roles.title": "System Roles",
    "workspace_settings.roles.description":
      "Create custom system roles and manage their permission sets.",
    "workspace_settings.roles.create_action": "Create system role",
    "workspace_settings.roles.search_placeholder": "Search system roles…",
    "workspace_settings.roles.column_role": "System Role",
    "workspace_settings.roles.empty": "No system roles yet.",
    "workspace_settings.roles.section_title": "Custom system roles",
    "workspace_settings.roles.section_description":
      "Create system role presets with explicit permission sets for workspace members.",
    "workspace_settings.roles.is_default": "Default system role for new members",

    // System Role editor + its confirmations and toasts
    "workspace_settings.roles.editor_create_title": "Create system role",
    "workspace_settings.roles.editor_description_editable":
      "Configure a custom system role and choose which permissions it grants.",
    // The shipped copy reads "System roles are read-only", which under the new
    // vocabulary says the opposite of what it means: it describes a *seeded*
    // role, not the concept the page is now named after.
    "workspace_settings.roles.editor_description_readonly":
      "This system role is read-only. Review the permission set below.",
    "workspace_settings.roles.create": "Create system role",
    "workspace_settings.roles.update": "Update system role",
    "workspace_settings.roles.create_success": "System role created",
    "workspace_settings.roles.update_success": "System role updated",
    "workspace_settings.roles.create_error": "Failed to create system role",
    "workspace_settings.roles.update_error": "Failed to update system role",
    "workspace_settings.roles.delete_confirm_title": "Delete system role",
    "workspace_settings.roles.delete_confirm_description":
      'Delete the custom system role "{name}"? Members currently assigned this system role will lose its permissions.',
    "workspace_settings.roles.delete_success": "System role deleted",
    "workspace_settings.roles.delete_error": "Failed to delete system role",

    // Members page column header, and the Invite Members dialog label — one key
    // feeds both (members-columns.tsx and workspace-invite-dialog.tsx).
    "workspace_settings.members.role_label": "System Role",
    "workspace_settings.members.role_updated": "Member system role updated",
    "workspace_settings.members.role_update_error": "Failed to update member system role",
    "workspace_settings.members.role_blocked_title": "User holds incompatible system roles:",

    // Pending-invitation landing page
    "invitation.role_label": "System Role",

    // Account notification for a member's role change
    "notifications.account.type.role": "System role updated",
    "notifications.account.field.role": "System Role",
  },
  fr: {
    // Navigation des paramètres d'espace de travail
    "workspace_settings.tabs.roles": "Rôles système",

    // Page Rôles système
    "workspace_settings.roles.title": "Rôles système",
    "workspace_settings.roles.description":
      "Créez des rôles système personnalisés et gérez leurs permissions.",
    "workspace_settings.roles.create_action": "Créer un rôle système",
    "workspace_settings.roles.search_placeholder": "Rechercher des rôles système…",
    "workspace_settings.roles.column_role": "Rôle système",
    "workspace_settings.roles.empty": "Aucun rôle système pour le moment.",
    "workspace_settings.roles.section_title": "Rôles système personnalisés",
    "workspace_settings.roles.section_description":
      "Créez des préréglages de rôle système avec des ensembles de permissions explicites.",
    "workspace_settings.roles.is_default": "Rôle système par défaut pour les nouveaux membres",

    // Éditeur de rôle système
    "workspace_settings.roles.editor_create_title": "Créer un rôle système",
    "workspace_settings.roles.editor_description_editable":
      "Configurez un rôle système personnalisé et choisissez les permissions accordées.",
    "workspace_settings.roles.editor_description_readonly":
      "Ce rôle système est en lecture seule. Consultez les permissions ci-dessous.",
    "workspace_settings.roles.create": "Créer le rôle système",
    "workspace_settings.roles.update": "Mettre à jour le rôle système",
    "workspace_settings.roles.create_success": "Rôle système créé",
    "workspace_settings.roles.update_success": "Rôle système mis à jour",
    "workspace_settings.roles.create_error": "Échec de la création du rôle système",
    "workspace_settings.roles.update_error": "Échec de la mise à jour du rôle système",
    "workspace_settings.roles.delete_confirm_title": "Supprimer le rôle système",
    "workspace_settings.roles.delete_confirm_description":
      "Supprimer le rôle système « {name} » ? Les membres qui en sont titulaires perdront ces permissions.",
    "workspace_settings.roles.delete_success": "Rôle système supprimé",
    "workspace_settings.roles.delete_error": "Échec de la suppression du rôle système",

    // Page Membres et boîte de dialogue d'invitation
    "workspace_settings.members.role_label": "Rôle système",
    "workspace_settings.members.role_updated": "Rôle système du membre mis à jour",
    "workspace_settings.members.role_update_error":
      "Échec de la mise à jour du rôle système du membre",
    "workspace_settings.members.role_blocked_title":
      "L'utilisateur détient des rôles système incompatibles :",

    // Page d'atterrissage d'invitation
    "invitation.role_label": "Rôle système",

    // Notification de compte pour un changement de rôle
    "notifications.account.type.role": "Rôle système mis à jour",
    "notifications.account.field.role": "Rôle système",
  },
};
